import { Box, Stack } from '@mui/material'
import { ReactComponent as FarmTable } from 'assets/farm_table.svg'
import { ReactComponent as MilkCup } from 'assets/milk-cup.svg'
import GroupCard from 'components/groups/GroupCard'
import { GroupHealthSummaryCard } from 'components/group/main-info/components/GroupHealthSummaryCard'
import { LinearChart } from 'components/groups/ui/linear-chart/LinearChart'
import { useGroupDailyStatistics } from 'hook/query/useGroupDailyStatistics'
import { IGroupEntity } from 'models'
import { ReactElement } from 'react'
import { useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import { CardStandart } from 'ui/CardStandart'
import { showMilkYieldData } from 'utils/companyMilkFeatures'

type KpiCardProps = {
	bulk: string
	count?: number | null
	ratio?: string
	isAlert?: boolean
	icon: ReactElement
	iconBgColor: string
	iconColor: string
}

const KpiCard = ({
	bulk,
	count,
	ratio,
	isAlert,
	icon,
	iconBgColor,
	iconColor,
}: KpiCardProps) => (
	<Box sx={{ height: '100%', position: 'relative' }}>
		<GroupCard bulk={bulk} count={count} ratio={ratio} isAlert={isAlert} />
		<Box
			sx={{
				position: 'absolute',
				left: '16px',
				bottom: '16px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: '22px',
				height: '22px',
				borderRadius: '7px',
				backgroundColor: iconBgColor,
				'& path': { stroke: `${iconColor} !important` },
			}}
		>
			{icon}
		</Box>
	</Box>
)

export const Indicators = (props: IGroupEntity) => {
	const { permissions } = useAppSelector(state => state.authReducer)
	const { userData } = useAppSelector(state => state.userReducer)
	const hasDigitizationLicense =
		permissions?.license_identification ||
		permissions?.license_fatness ||
		permissions?.license_limpness
	const showMilkYield = Boolean(
		hasDigitizationLicense &&
			showMilkYieldData(
				userData?.company?.id,
				userData?.company?.dairy_integration,
			),
	)
	const showHealth =
		permissions?.license_limpness || permissions?.license_fatness
	const { data: dailyStatistics } = useGroupDailyStatistics(
		props.id,
		Boolean(showHealth),
	)
	const distributionWeight = Number(props.previous_distribution_weight)
	const cleaningWeight = Number(props.previous_cleaning_weight)
	const leftoversPercent =
		distributionWeight > 0 ? (cleaningWeight / distributionWeight) * 100 : null
	const isLeftoversAlert =
		leftoversPercent != null && (leftoversPercent <= 3 || leftoversPercent >= 6)
	const showMetrics = permissions?.license_food_table || showMilkYield
	const metricsCount =
		(permissions?.license_food_table ? 3 : 0) + (showMilkYield ? 1 : 0)
	const showMetricsInSingleRow =
		metricsCount === 1 || (!showHealth && metricsCount < 4)
	const singleRowMetricsWidth =
		metricsCount * 172 + Math.max(metricsCount - 1, 0) * 16
	const showAllSummaryWidgets = Boolean(
		showMetrics &&
			permissions?.license_limpness &&
			permissions?.license_fatness,
	)

	if (!permissions?.license_food_table && !showMilkYield && !showHealth)
		return null

	const metricsBlock = showMetrics ? (
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: {
					xs: '1fr',
					sm: showMetricsInSingleRow
						? `repeat(${metricsCount}, minmax(0, 1fr))`
						: 'repeat(2, minmax(0, 1fr))',
				},
				gridTemplateRows: showMetricsInSingleRow
					? 'minmax(0, 1fr)'
					: 'repeat(2, minmax(0, 1fr))',
				gap: '16px',
				width: '100%',
				height: '100%',
			}}
		>
			{permissions?.license_food_table && (
				<>
					<KpiCard
						bulk='Роздано корма'
						count={
							userData?.isFoodTableExpired
								? undefined
								: props.previous_distribution_weight
						}
						ratio={userData?.isFoodTableExpired ? undefined : 'кг'}
						icon={<FarmTable />}
						iconBgColor={COLORS.mainSecondaryOpacity}
						iconColor={COLORS.main}
					/>
					<KpiCard
						bulk='Суммарный остаток'
						count={
							userData?.isFoodTableExpired
								? undefined
								: props.previous_cleaning_weight
						}
						ratio={userData?.isFoodTableExpired ? undefined : 'кг'}
						isAlert={!userData?.isFoodTableExpired && isLeftoversAlert}
						icon={<FarmTable />}
						iconBgColor={COLORS.mainSecondaryOpacity}
						iconColor={COLORS.main}
					/>
					<KpiCard
						bulk='ПСВ'
						count={
							userData?.isFoodTableExpired ? undefined : props.food_dry_per_head
						}
						ratio={userData?.isFoodTableExpired ? undefined : 'кг/голову'}
						icon={<FarmTable />}
						iconBgColor={COLORS.mainSecondaryOpacity}
						iconColor={COLORS.main}
					/>
				</>
			)}
			{showMilkYield && (
				<KpiCard
					bulk='Фактический надой'
					count={
						userData?.isIdentificationExpired
							? undefined
							: props.milk_yesterday_total
					}
					ratio={userData?.isIdentificationExpired ? undefined : 'л'}
					icon={<MilkCup />}
					iconBgColor={COLORS.blueBg}
					iconColor={COLORS.blue}
				/>
			)}
		</Box>
	) : null

	return (
		<Stack gap='24px'>
			{permissions?.license_food_table && (
				<CardStandart sx={{ padding: '24px', width: '100%' }}>
					<LinearChart {...props} showTitle />
				</CardStandart>
			)}

			{(showMetrics || showHealth) && (
				<Box
					sx={{
						display: 'flex',
						flexWrap: {
							xs: 'wrap',
							lg: showAllSummaryWidgets ? 'nowrap' : 'wrap',
						},
						alignItems: 'flex-start',
						gap: '16px',
						width: '100%',
					}}
				>
					{metricsBlock && (
						<Box
							sx={{
								flex: showMetricsInSingleRow
									? `0 1 ${singleRowMetricsWidth}px`
									: '0 1 360px',
								width: '100%',
								maxWidth: showMetricsInSingleRow
									? `${singleRowMetricsWidth}px`
									: '360px',
								height: showMetricsInSingleRow ? '162px' : '340px',
								minWidth: 0,
								display: 'flex',
							}}
						>
							{metricsBlock}
						</Box>
					)}
					{permissions?.license_limpness && (
						<GroupHealthSummaryCard
							title='Хромота'
							items={
								userData?.isLamenessExpired
									? []
									: dailyStatistics?.limpness_data
							}
							totalCattle={props.cattles_qty}
							totalAffected={props.limp_cattle}
							groupId={props.id}
							variant='lameness'
							fillAvailableWidth={showAllSummaryWidgets}
						/>
					)}
					{permissions?.license_fatness && (
						<GroupHealthSummaryCard
							title='Упитанность'
							items={
								userData?.isFatnessExpired ? [] : dailyStatistics?.fatness_data
							}
							totalCattle={props.cattles_qty}
							groupId={props.id}
							variant='fatness'
							fillAvailableWidth={showAllSummaryWidgets}
						/>
					)}
				</Box>
			)}
		</Stack>
	)
}
